// WebSocket chat. Chiều server→client đẩy event {type:"message"|"session"|
// "presence"|"message_updated"}; chiều client→server CHỈ có một loại khung: {type:"viewing"}
// báo mình đang mở cuộc chat nào (quyết định 0063 mục 6). Gửi TIN vẫn đi
// đường REST. Tự nối lại với backoff 1s→10s; url=null nghĩa là tắt.
import { useCallback, useEffect, useRef } from "react";
import type { ChatSocketEvent } from "./api";

export function useChatSocket(
  url: string | null,
  onEvent: (event: ChatSocketEvent) => void,
): { setViewing: (sessionId: string) => void } {
  // Ref để đổi handler không làm socket nối lại theo mỗi render.
  const handlerRef = useRef(onEvent);
  useEffect(() => {
    handlerRef.current = onEvent;
  });

  const socketRef = useRef<WebSocket | null>(null);
  // Nhớ phiên đang mở để gửi lại sau mỗi lần nối lại. Không có dòng này thì
  // socket rớt rồi nối lại là máy chủ tưởng không ai đang mở — "đang mở" tắt
  // ngóm mà không ai biết vì sao.
  const viewingRef = useRef<string>("");

  const setViewing = useCallback((sessionId: string) => {
    viewingRef.current = sessionId;
    const s = socketRef.current;
    if (s && s.readyState === WebSocket.OPEN) {
      s.send(JSON.stringify({ type: "viewing", sessionId }));
    }
  }, []);

  useEffect(() => {
    if (!url || typeof WebSocket === "undefined") return;
    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let closed = false;

    const connect = () => {
      if (closed) return;
      socket = new WebSocket(url);
      socketRef.current = socket;
      socket.onopen = () => {
        attempts = 0;
        if (viewingRef.current) {
          socket?.send(JSON.stringify({ type: "viewing", sessionId: viewingRef.current }));
        }
      };
      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(String(event.data)) as ChatSocketEvent;
          if (
            parsed &&
            (parsed.type === "message" ||
              parsed.type === "session" ||
              parsed.type === "presence" ||
              parsed.type === "message_updated")
          ) {
            handlerRef.current(parsed);
          }
        } catch {
          // Bỏ qua frame không phải JSON — resync đã có refetchInterval lo.
        }
      };
      socket.onclose = () => {
        if (closed) return;
        attempts += 1;
        const delay = Math.min(1000 * 2 ** Math.min(attempts, 4), 10_000);
        retryTimer = setTimeout(connect, delay);
      };
      socket.onerror = () => {
        socket?.close();
      };
    };

    connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      socket?.close();
      socketRef.current = null;
    };
  }, [url]);

  return { setViewing };
}
